import React from 'react'
import styled, { css } from 'styled-components'
import { useKeyPress, useMediaQuery } from '@mote/helpers'
import { isNumber } from '@mote/helpers'
import { KeyboardKey } from '@mote/helpers/constants'
import { Block } from '@mote/ui'

export const Sidebar = ({
  isOpen,
  onToggle,
  items = [],
  children,
  position = 'left',
  hideOverlay = false,
  isCollapsible = true,
  breakpoints = [640, 920, 1200],
  collapseBreakpoint = 920,
  width = 256,
  ...rest
}) => {
  const ref = React.useRef(null)
  const mediaBreakPoint = useMediaQuery(breakpoints)
  const isMobile = React.useMemo(
    () => mediaBreakPoint <= collapseBreakpoint,
    [mediaBreakPoint, collapseBreakpoint]
  )

  function outSideEventHandler() {
    if ((!isMobile && isCollapsible) || (isMobile && !isOpen)) return

    onToggle()
  }

  useKeyPress(KeyboardKey.Escape, outSideEventHandler, {}, [isOpen, onToggle])

  return (
    <>
      {!hideOverlay && (isMobile || (!isMobile && !isCollapsible)) && (
        <Overlay isOpen={isOpen} onClick={onToggle} />
      )}
      <Root
        ref={ref}
        layout='vertical'
        isOpen={isOpen}
        position={position}
        width={width}
        {...rest}
      >
        {children}
      </Root>
    </>
  )
}

const SidebarHeader = props => (
  <Header layout='horizontal' align='center' spreadX {...props} />
)
const SidebarContent = props => (
  <Content layout='vertical' spreadX spreadY {...props} />
)

Sidebar.Header = SidebarHeader
Sidebar.Content = SidebarContent

const Root = styled(Block)`
  position: fixed;
  z-index: 50;
  top: 0;
  width: ${({ width }) => (isNumber(width) ? `${width}px` : width)};
  height: 100%;
  min-height: 100vh;
  padding: 16px;
  flex-shrink: 0;
  opacity: 0;
  visibility: hidden;
  background: #f6f6f9;
  overflow: hidden;
  transition-property: transform, opacity, visibility;
  transition-duration: 150ms;
  transition-timing-function: ease-in-out;

  ${({ position }) => {
    switch (position) {
      case 'left':
        return css`
          left: 0;
          transform: translateX(-100%);
        `
      case 'right':
        return css`
          right: 0;
          transform: translateX(100%);
        `
      default:
        return ''
    }
  }};

  ${({ isOpen }) =>
    isOpen &&
    css`
      opacity: 1;
      visibility: visible;
      transform: translateX(0);
    `}
`
const Overlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 40;
  opacity: 0;
  visibility: hidden;
  background: rgba(113, 101, 129, 0.25);
  transition: all 150ms ease-in-out;

  ${({ isOpen }) =>
    isOpen &&
    css`
      opacity: 1;
      visibility: visible;
    `}
`

const Content = styled(Block)`
  overflow-y: auto;
  padding: 16px 0;
  height: 100%;
`

const Header = styled(Block)`
  padding: 16px 0;
  background: #f6f6f9;
  flex-shrink: 0;
`
